import { ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/common/Button';
import { ZoomableImage } from '@/components/common/ImageLightbox';

const easeOutQuint = [0.22, 1, 0.36, 1] as const;

export function HeroSection() {
  const reduceMotion = useReducedMotion();

  return (
    <section className="overflow-hidden pb-16 pt-27 sm:pb-20 sm:pt-31 lg:pb-23 lg:pt-32">
      <div className="site-container grid items-center gap-10 lg:grid-cols-[0.88fr_1.12fr] lg:gap-14 xl:gap-18">
        <div className="relative z-10">
          <motion.h1
            className="max-w-[12ch] text-balance text-[2.65rem] font-semibold leading-[1.02] tracking-[-0.06em] text-text-primary sm:text-[3.85rem] lg:text-[4rem] xl:text-[4.35rem]"
            initial={reduceMotion ? false : { y: 28 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.65, ease: easeOutQuint }}
          >
            Vodimo poslovne projekte od odluke do{' '}
            <span className="text-primary">realizacije.</span>
          </motion.h1>
          <motion.p
            className="mt-6 max-w-[34rem] text-pretty text-base leading-7 text-text-secondary sm:text-[1.05rem] sm:leading-8"
            initial={reduceMotion ? false : { y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.55, delay: 0.1, ease: easeOutQuint }}
          >
            Za mala i srednja preduzeća pripremamo plan, organizujemo učesnike i
            pratimo rokove. Klijent u svakom trenutku zna šta je završeno i šta
            sledi.
          </motion.p>

          <motion.div
            className="mt-7 flex flex-col gap-3 sm:flex-row"
            initial={reduceMotion ? false : { y: 16 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.18, ease: easeOutQuint }}
          >
            <Button to="/usluge" size="lg">
              Pogledajte usluge
              <ArrowRight aria-hidden="true" className="size-4" />
            </Button>
            <Button to="/kontakt" size="lg" variant="outline">
              Zatražite razgovor
            </Button>
          </motion.div>
        </div>

        <motion.figure
          className="lg:-mr-5 xl:-mr-9"
          initial={reduceMotion ? false : { y: 14, scale: 0.985 }}
          animate={{ y: 0, scale: 1 }}
          transition={{ duration: 0.56, delay: 0.04, ease: easeOutQuint }}
        >
          <ZoomableImage
            className="aspect-[4/3.35] w-full rounded-[var(--radius-md)] object-cover shadow-[0_16px_40px_rgba(17,24,18,0.12)] sm:aspect-[4/3] lg:aspect-[4/3.65] dark:shadow-none"
            src="/images/hero-office-960.webp"
            srcSet="/images/hero-office-640.webp 640w, /images/hero-office-768.webp 768w, /images/hero-office-960.webp 960w, /images/hero-office-1280.webp 1280w, /images/hero-office-1600.webp 1600w"
            sizes="(min-width: 1440px) 730px, (min-width: 1024px) 54vw, (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)"
            sources={[
              {
                type: 'image/avif',
                srcSet:
                  '/images/hero-office-640.avif 640w, /images/hero-office-768.avif 768w, /images/hero-office-960.avif 960w, /images/hero-office-1280.avif 1280w, /images/hero-office-1600.avif 1600w'
              }
            ]}
            alt="Enterijer savremenog prostora za sastanke"
            width={1600}
            height={1067}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <figcaption className="mt-3 flex justify-between gap-4 text-xs leading-5 text-text-muted">
            <span>Planiranje i koordinacija</span>
            <span>Beograd · Srbija</span>
          </figcaption>
        </motion.figure>
      </div>
    </section>
  );
}
